import React, { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "../../Components/Sidebar/Sidebar";
import Header from "../../Components/Header/Header";
import classes from "./Appointments.module.css";
import Newappointment from "./Newappointment";
import {
    FaCalendarCheck, FaCalendarWeek, FaCalendarPlus, FaCheckCircle, FaTimesCircle,
    FaArrowUp, FaArrowDown, FaChevronLeft, FaChevronRight, FaUser, FaPlus, FaFileAlt,
    FaCalendarAlt, FaSpinner, FaSync
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

/* ===========================
   SKELETON LOADER COMPONENTS (Table Only)
=========================== */
const SkeletonScheduleRow = () => (
    <tr className={classes.skeletonRow}>
        <td><div className={classes.skeletonTime}></div></td>
        {[1, 2, 3, 4].map((i) => (
            <td key={i}><div className={classes.skeletonAppt}></div></td>
        ))}
    </tr>
);

/* ===========================
   DATA & CONFIG
=========================== */
const statsData = [
    { title: "Today's Appointments", val: "0", change: "0%", dir: "up", color: "#2563eb", bg: "#eff6ff", icon: <FaCalendarCheck /> },
    { title: "This Week", val: "0", change: "0%", dir: "up", color: "#059669", bg: "#ecfdf5", icon: <FaCalendarWeek /> },
    { title: "Upcoming", val: "0", change: "0%", dir: "up", color: "#7c3aed", bg: "#f5f3ff", icon: <FaCalendarPlus /> },
    { title: "Completed", val: "0", change: "0%", dir: "up", color: "#f59e0b", bg: "#fef3c7", icon: <FaCheckCircle /> },
    { title: "Cancelled", val: "0", change: "0%", dir: "down", color: "#ef4444", bg: "#fef2f2", icon: <FaTimesCircle /> },
];

/* ===========================
   MAIN COMPONENT
=========================== */
export default function Appointments() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [view, setView] = useState("Day");
    const [date, setDate] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(true);
    const [error, setError] = useState("");
    const [appointments, setAppointments] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [stats, setStats] = useState(statsData);
    const [upcomingAppts, setUpcomingAppts] = useState([]);
    const [appointmentMap, setAppointmentMap] = useState({});

    /* --- FILTER STATES --- */
    const [filters, setFilters] = useState({
        doctor: "All Doctors",
        department: "All Departments",
        status: "All Status"
    });

    const [displayedDoctors, setDisplayedDoctors] = useState([]);

    const timeSlots = ["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"];
    const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    const generateCalendarDays = () => {
        const days = [];
        for (let i = 28; i <= 31; i++) days.push(i);
        for (let i = 1; i <= 30; i++) days.push(i);
        for (let i = 1; i <= 2; i++) days.push(i);
        return days;
    };
    const calendarDays = generateCalendarDays();

    // Get company ID
    const getCompanyId = () => {
        return parseInt(localStorage.getItem("companyId") || "1");
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    // Format time
    const formatTime = (timeString) => {
        if (!timeString) return "N/A";
        try {
            // Handle "13:35:00" format
            const parts = timeString.split(':');
            if (parts.length >= 2) {
                const h = parseInt(parts[0]);
                const m = parts[1];
                const ampm = h >= 12 ? 'PM' : 'AM';
                const h12 = h % 12 || 12;
                return `${h12}:${m} ${ampm}`;
            }
            return timeString;
        } catch {
            return timeString;
        }
    };

    // Convert time to 24-hour format for comparison
    const convertTo24Hour = (timeString) => {
        if (!timeString) return "";
        try {
            // If already in 24-hour format (e.g., "13:35")
            if (timeString.match(/^\d{2}:\d{2}/)) {
                return timeString.substring(0, 5);
            }
            // Convert from 12-hour format
            const match = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
            if (match) {
                let hours = parseInt(match[1]);
                const minutes = match[2];
                const ampm = match[3].toUpperCase();
                if (ampm === 'PM' && hours !== 12) hours += 12;
                if (ampm === 'AM' && hours === 12) hours = 0;
                return `${String(hours).padStart(2, '0')}:${minutes}`;
            }
            return timeString;
        } catch {
            return timeString;
        }
    };

    // Format display time for the table (12-hour format)
    const formatDisplayTime = (timeString) => {
        if (!timeString) return "N/A";
        try {
            const [hours, minutes] = timeString.split(':');
            const h = parseInt(hours);
            const ampm = h >= 12 ? 'PM' : 'AM';
            const h12 = h % 12 || 12;
            return `${h12}:${minutes} ${ampm}`;
        } catch {
            return timeString;
        }
    };

    // Get doctor display name
    const getDoctorName = (doctor) => {
        if (!doctor) return "Unknown Doctor";
        if (doctor.name) return doctor.name;
        return `Dr. ${doctor.first_name || ''} ${doctor.last_name || ''}`.trim() || "Unknown Doctor";
    };

    // Get patient display name
    const getPatientName = (patient) => {
        if (!patient) return "Unknown Patient";
        if (patient.name) return patient.name;
        return `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || "Unknown Patient";
    };

    // Get patient ID
    const getPatientId = (patient) => {
        if (!patient) return "N/A";
        return patient.patient_no || `ID: ${patient.id}`;
    };

    // Fetch appointments
    const fetchAppointments = async () => {
        setLoading(true);
        setTableLoading(true);
        setError("");
        try {
            const companyId = getCompanyId();
            const response = await axios.get(`${BASE_URL}/api/appointment`, {
                params: {
                    company_id: companyId,
                    per_page: 100
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
            });

            console.log("Appointments API Response:", response.data);

            let appointmentsData = [];
            if (response.data && response.data.data) {
                const data = response.data.data.data || response.data.data;
                appointmentsData = Array.isArray(data) ? data : [data];
            }

            setAppointments(appointmentsData);
            
            // Build appointment map for quick lookup
            const map = {};
            appointmentsData.forEach(appt => {
                const doctorName = getDoctorName(appt.doctor);
                const time = appt.appointment_time;
                const time24 = convertTo24Hour(time);
                const key = `${doctorName}_${time24}`;
                map[key] = appt;
            });
            setAppointmentMap(map);
            
            updateStats(appointmentsData);
            updateUpcomingAppointments(appointmentsData);
            extractFilters(appointmentsData);

        } catch (error) {
            console.error("Error fetching appointments:", error);
            let errorMsg = "Failed to load appointments. Please try again.";
            if (error.response) {
                if (error.response.status === 401) {
                    errorMsg = "Unauthorized. Please login again.";
                } else if (error.response.data?.message) {
                    errorMsg = error.response.data.message;
                }
            }
            setError(errorMsg);
        } finally {
            setLoading(false);
            setTableLoading(false);
        }
    };

    // Extract unique doctors and departments from appointments
    const extractFilters = (appointmentsData) => {
        const uniqueDoctors = [];
        const uniqueDepartments = [];
        const doctorMap = {};
        const deptMap = {};

        appointmentsData.forEach(appt => {
            if (appt.doctor && !doctorMap[appt.doctor.id]) {
                doctorMap[appt.doctor.id] = true;
                uniqueDoctors.push({
                    id: appt.doctor.id,
                    name: getDoctorName(appt.doctor),
                    specialty: appt.doctor.specialization || appt.doctor.department || ''
                });
            }
            if (appt.department && !deptMap[appt.department.id]) {
                deptMap[appt.department.id] = true;
                uniqueDepartments.push(appt.department.name);
            }
        });

        if (uniqueDoctors.length === 0) {
            fetchDoctors();
        } else {
            setDoctors(uniqueDoctors);
            setDisplayedDoctors(uniqueDoctors);
        }

        setDepartments(uniqueDepartments);
    };

    // Fetch doctors separately if needed
    const fetchDoctors = async () => {
        try {
            const companyId = getCompanyId();
            const response = await axios.get(`${BASE_URL}/api/doctor`, {
                params: { company_id: companyId },
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
            });
            if (response.data && response.data.data) {
                const data = response.data.data.data || response.data.data;
                const doctorsList = Array.isArray(data) ? data : [data];
                setDoctors(doctorsList.map(d => ({
                    id: d.id,
                    name: `Dr. ${d.first_name || ''} ${d.last_name || ''}`,
                    specialty: d.specialization || d.department || ''
                })));
                setDisplayedDoctors(doctorsList.map(d => ({
                    id: d.id,
                    name: `Dr. ${d.first_name || ''} ${d.last_name || ''}`,
                    specialty: d.specialization || d.department || ''
                })));
            }
        } catch (error) {
            console.error("Error fetching doctors:", error);
        }
    };

    // Update stats
    const updateStats = (appointmentsData) => {
        const today = new Date().toISOString().split('T')[0];
        const todayAppts = appointmentsData.filter(a => a.appointment_date?.split('T')[0] === today);
        
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekStartStr = weekStart.toISOString().split('T')[0];
        const weekAppts = appointmentsData.filter(a => a.appointment_date?.split('T')[0] >= weekStartStr);
        
        const completed = appointmentsData.filter(a => a.status?.toLowerCase() === "completed");
        const cancelled = appointmentsData.filter(a => a.status?.toLowerCase() === "cancelled");
        const upcoming = appointmentsData.filter(a => a.status?.toLowerCase() === "pending" || a.status?.toLowerCase() === "scheduled");

        setStats([
            { title: "Today's Appointments", val: todayAppts.length.toString(), change: "0%", dir: "up", color: "#2563eb", bg: "#eff6ff", icon: <FaCalendarCheck /> },
            { title: "This Week", val: weekAppts.length.toString(), change: "0%", dir: "up", color: "#059669", bg: "#ecfdf5", icon: <FaCalendarWeek /> },
            { title: "Upcoming", val: upcoming.length.toString(), change: "0%", dir: "up", color: "#7c3aed", bg: "#f5f3ff", icon: <FaCalendarPlus /> },
            { title: "Completed", val: completed.length.toString(), change: "0%", dir: "up", color: "#f59e0b", bg: "#fef3c7", icon: <FaCheckCircle /> },
            { title: "Cancelled", val: cancelled.length.toString(), change: "0%", dir: "down", color: "#ef4444", bg: "#fef2f2", icon: <FaTimesCircle /> },
        ]);
    };

    // Update upcoming appointments
    const updateUpcomingAppointments = (appointmentsData) => {
        const today = new Date().toISOString().split('T')[0];
        const upcoming = appointmentsData
            .filter(a => a.appointment_date?.split('T')[0] >= today)
            .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date))
            .slice(0, 5);

        setUpcomingAppts(upcoming.map(a => ({
            name: getPatientName(a.patient),
            id: getPatientId(a.patient),
            doc: getDoctorName(a.doctor),
            status: a.status || "Pending",
            time: a.appointment_time || "N/A",
            date: a.appointment_date
        })));
    };

    // Load data on mount
    useEffect(() => {
        fetchAppointments();
    }, []);

    // Handle filter change
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // Apply filters
    const handleApplyFilters = () => {
        let filtered = [...doctors];

        if (filters.doctor !== "All Doctors") {
            filtered = filtered.filter(doc => doc.name === filters.doctor);
        }
        if (filters.department !== "All Departments") {
            filtered = filtered.filter(doc => doc.specialty === filters.department);
        }
        setDisplayedDoctors(filtered);
    };

    // Reset filters
    const handleResetFilters = () => {
        setFilters({
            doctor: "All Doctors",
            department: "All Departments",
            status: "All Status"
        });
        setDisplayedDoctors(doctors);
    };

    // Get doctor color
    const getDoctorColor = (doctorName) => {
        const colors = [
            { bg: "#EFF6FF", border: "#3B82F6", text: "#1D4ED8" },
            { bg: "#ECFDF5", border: "#10B981", text: "#047857" },
            { bg: "#FEF3C7", border: "#F59E0B", text: "#B45309" },
            { bg: "#F3E8FF", border: "#8B5CF6", text: "#6D28D9" },
            { bg: "#FCE4EC", border: "#EC407A", text: "#C2185B" },
            { bg: "#E0F7FA", border: "#26C6DA", text: "#00838F" },
        ];
        let hash = 0;
        for (let i = 0; i < doctorName.length; i++) {
            hash = doctorName.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    // Get appointment status class
    const getStatusClass = (status) => {
        if (!status) return classes.statusPending;
        const s = status.toLowerCase();
        if (s === "confirmed" || s === "active" || s === "completed") return classes.statusConfirmed;
        if (s === "cancelled" || s === "inactive") return classes.statusCancelled;
        return classes.statusPending;
    };

    // Navigate date
    const changeDate = (offset) => {
        const newDate = new Date(date);
        newDate.setDate(newDate.getDate() + offset);
        setDate(newDate);
    };

    const formatDisplayDate = (dateObj) => {
        return dateObj.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Render skeleton schedule rows
    const renderSkeletonScheduleRows = () => {
        return Array(5).fill(0).map((_, index) => (
            <SkeletonScheduleRow key={`skeleton-row-${index}`} />
        ));
    };

    // Render schedule cells for a specific time slot
    const renderScheduleCells = (time) => {
        if (!displayedDoctors.length) {
            return <td colSpan="4" style={{ textAlign: 'center', color: '#9CA3AF', padding: '20px' }}>
                No doctors match the selected filters.
            </td>;
        }

        return displayedDoctors.map((doc, dIdx) => {
            const isLunch = time === "12:00 PM" || time === "12:30 PM";

            if (isLunch) {
                return (
                    <td key={dIdx}>
                        <div className={`${classes.apptCard} ${classes.lunchCard}`}>
                            12:00 PM - 01:00 PM<br />Lunch Break
                        </div>
                    </td>
                );
            }

            // Find appointment for this doctor at this time
            // Convert time to 24-hour format for matching
            const time24 = convertTo24Hour(time);
            const doctorName = doc.name;
            
            // Find appointment by matching doctor name and time
            const appt = appointments.find(a => {
                const aDoctorName = getDoctorName(a.doctor);
                const aTime = a.appointment_time;
                const aTime24 = convertTo24Hour(aTime);
                return aDoctorName === doctorName && aTime24 === time24;
            });

            if (!appt) {
                return <td key={dIdx}></td>;
            }

            const docColor = getDoctorColor(doc.name);
            const status = appt.status || "Pending";

            return (
                <td key={dIdx}>
                    <div className={classes.apptCard} style={{ backgroundColor: docColor.bg, borderColor: docColor.border }}>
                        <h5>{getPatientName(appt.patient)}</h5>
                        <p>{formatDisplayTime(appt.appointment_time)}</p>
                        <span className={`${classes.apptStatusLabel} ${getStatusClass(status)}`}>
                            {status}
                        </span>
                    </div>
                </td>
            );
        });
    };

    return (
        <div className={classes.page}>
            <Sidebar />
            <div className={classes.mainWrapper}>
                <Header />
                <div className={classes.contentArea}>

                    {/* --- HEADER & STATS --- */}
                    <div className={classes.topSection}>
                        <div>
                            <h1 className={classes.pageTitle}>Appointments</h1>
                            <p className={classes.breadcrumb}>Manage patient appointments and schedule</p>
                        </div>
                        <div className={classes.topButtons}>
                            <button className={classes.btnPrimary} onClick={() => setIsModalOpen(true)}>
                                <FaPlus /> New Appointment
                            </button>
                            <button className={classes.btnOutline} onClick={fetchAppointments} disabled={loading}>
                                <FaSync className={loading ? classes.spinning : ''} /> Refresh
                            </button>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className={classes.errorMessage}>
                            <span>{error}</span>
                            <button onClick={fetchAppointments} className={classes.retryBtn}>Retry</button>
                        </div>
                    )}

                    {/* Stats Cards */}
                    <div className={classes.statsGrid}>
                        {stats.map((s, i) => (
                            <div key={i} className={classes.statCard}>
                                <div className={classes.iconBox} style={{ backgroundColor: s.bg, color: s.color }}>
                                    {s.icon}
                                </div>
                                <div className={classes.statContent}>
                                    <p className={classes.statTitle}>{s.title}</p>
                                    <div className={classes.statValue}>{s.val}</div>
                                    <div className={`${classes.statChange} ${s.dir === 'up' ? classes.trendUp : classes.trendDown}`}>
                                        {s.dir === 'up' ? <FaArrowUp /> : <FaArrowDown />}
                                        <span>{s.change}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* --- 3-COLUMN GRID --- */}
                    <div className={classes.middleSection}>

                        {/* --- LEFT: FILTERS --- */}
                        <div className={classes.filterPanel}>
                            <h3>Filters <span className={classes.resetLink} onClick={handleResetFilters}>Reset</span></h3>

                            <div className={classes.filterGroup}>
                                <label>Date</label>
                                <input type="text" value={formatDisplayDate(date)} readOnly className={classes.filterInput} />
                            </div>

                            <div className={classes.filterGroup}>
                                <label>Doctor</label>
                                <select name="doctor" value={filters.doctor} onChange={handleFilterChange} className={classes.filterSelect}>
                                    <option>All Doctors</option>
                                    {doctors.map(d => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
                                </select>
                            </div>

                            <div className={classes.filterGroup}>
                                <label>Department</label>
                                <select name="department" value={filters.department} onChange={handleFilterChange} className={classes.filterSelect}>
                                    <option>All Departments</option>
                                    {departments.map(dept => (
                                        <option key={dept} value={dept}>{dept}</option>
                                    ))}
                                </select>
                            </div>

                            <div className={classes.filterGroup}>
                                <label>Status</label>
                                <select name="status" value={filters.status} onChange={handleFilterChange} className={classes.filterSelect}>
                                    <option>All Status</option>
                                    <option>Confirmed</option>
                                    <option>Pending</option>
                                    <option>Cancelled</option>
                                    <option>Completed</option>
                                </select>
                            </div>

                            <button className={classes.btnApplyFilters} onClick={handleApplyFilters}>Apply Filters</button>
                        </div>

                        {/* --- MIDDLE: SCHEDULE with Skeleton --- */}
                        <div className={classes.schedulePanel}>
                            {tableLoading ? (
                                <div className={classes.scheduleLoading}>
                                    <div className={classes.scheduleNavSkeleton}>
                                        <div className={classes.skeletonViewToggles}>
                                            {[1, 2, 3].map((i) => (
                                                <div key={i} className={classes.skeletonViewBtn}></div>
                                            ))}
                                        </div>
                                        <div className={classes.skeletonDateNav}>
                                            <div className={classes.skeletonNavBtn}></div>
                                            <div className={classes.skeletonDateText}></div>
                                            <div className={classes.skeletonNavBtn}></div>
                                            <div className={classes.skeletonTodayBtn}></div>
                                        </div>
                                    </div>
                                    <table className={classes.gridTable}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '80px' }}>Time</th>
                                                {[1, 2, 3, 4].map((i) => (
                                                    <th key={i}>
                                                        <div className={classes.skeletonDoctorHeader}></div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {renderSkeletonScheduleRows()}
                                        </tbody>
                                    </table>
                                    <div className={classes.skeletonLegend}>
                                        {[1, 2, 3, 4].map((i) => (
                                            <div key={i} className={classes.skeletonLegendItem}></div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className={classes.scheduleNav}>
                                        <div className={classes.viewToggles}>
                                            {['Day', 'Week', 'Month'].map(m => (
                                                <button key={m} className={`${classes.viewBtn} ${view === m ? classes.activeView : ''}`} onClick={() => setView(m)}>
                                                    {m}
                                                </button>
                                            ))}
                                        </div>
                                        <div className={classes.dateNav}>
                                            <button className={classes.navBtn} onClick={() => changeDate(-1)}><FaChevronLeft /></button>
                                            <span><FaCalendarAlt /> {formatDisplayDate(date)}</span>
                                            <button className={classes.navBtn} onClick={() => changeDate(1)}><FaChevronRight /></button>
                                            <button className={classes.todayBtn} onClick={() => setDate(new Date())}>Today</button>
                                        </div>
                                    </div>

                                    <table className={classes.gridTable}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '80px' }}>Time</th>
                                                {displayedDoctors.length > 0 ? (
                                                    displayedDoctors.map(d => (
                                                        <th key={d.id || d.name}>{d.name}<span>{d.specialty}</span></th>
                                                    ))
                                                ) : (
                                                    <th colSpan="4" style={{ textAlign: 'center', color: '#9CA3AF', padding: '20px' }}>
                                                        No doctors match the selected filters.
                                                    </th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {timeSlots.map((time) => {
                                                const time24 = convertTo24Hour(time);
                                                return (
                                                    <tr key={time}>
                                                        <td className={classes.timeLabel}>{time}</td>
                                                        {renderScheduleCells(time)}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    <div className={classes.scheduleLegend}>
                                        <span><span className={classes.legendDot} style={{ background: '#22c55e' }}></span> Confirmed</span>
                                        <span><span className={classes.legendDot} style={{ background: '#f59e0b' }}></span> Pending</span>
                                        <span><span className={classes.legendDot} style={{ background: '#ef4444' }}></span> Cancelled</span>
                                        <span><span className={classes.legendDot} style={{ background: '#2563eb' }}></span> Completed</span>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* --- RIGHT: SIDEBAR --- */}
                        <div className={classes.rightSection}>
                            <div className={classes.sidebarCard}>
                                <h3>Mini Calendar <span className={classes.cardAction}><FaChevronLeft /> <FaChevronRight /></span></h3>
                                <div className={classes.miniCalendarGrid}>
                                    {weekDays.map(d => <div key={d} className={classes.weekDayLabel}>{d}</div>)}
                                    {calendarDays.map((d, i) => {
                                        const today = new Date().getDate();
                                        return (
                                            <div key={i} className={`${classes.calDay} ${i < 4 || i > 34 ? classes.otherMonth : ''} ${d === today ? classes.todayCal : ''}`}>
                                                {d}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className={classes.sidebarCard}>
                                <h3>Upcoming Appointments <span className={classes.cardAction}>View All</span></h3>
                                <div className={classes.upcomingList}>
                                    {upcomingAppts.length > 0 ? (
                                        upcomingAppts.map((a, i) => {
                                            const docColor = getDoctorColor(a.doc);
                                            return (
                                                <div key={i} className={classes.upcomingItem}>
                                                    <div className={classes.upcomingColorBar} style={{ backgroundColor: docColor.border }}></div>
                                                    <div className={classes.upcomingInfo}>
                                                        <h4>{a.name}</h4>
                                                        <p>{a.id}</p>
                                                        <p className={classes.upcomingDoc}>{a.doc}</p>
                                                        <span className={`${classes.upcomingStatus} ${getStatusClass(a.status)}`}>
                                                            {a.status}
                                                        </span>
                                                    </div>
                                                    <div className={classes.upcomingTime}>
                                                        <strong>{formatDisplayTime(a.time)}</strong>
                                                        <span>{formatDate(a.date)}</span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className={classes.noData}>No upcoming appointments</div>
                                    )}
                                </div>
                            </div>

                            <div className={classes.sidebarCard}>
                                <h3>Appointment Status (Today)</h3>
                                <div className={classes.donutContainer}>
                                    <div className={classes.donutChart}></div>
                                    <div className={classes.donutCenter}>
                                        {stats[0]?.val || 0} <small>Total</small>
                                    </div>
                                </div>
                                <div className={classes.statusList}>
                                    {[
                                        { label: "Confirmed", val: appointments.filter(a => a.status?.toLowerCase() === "confirmed" || a.status?.toLowerCase() === "active").length, color: "#22c55e" },
                                        { label: "Pending", val: appointments.filter(a => a.status?.toLowerCase() === "pending" || a.status?.toLowerCase() === "scheduled").length, color: "#f59e0b" },
                                        { label: "Cancelled", val: appointments.filter(a => a.status?.toLowerCase() === "cancelled" || a.status?.toLowerCase() === "inactive").length, color: "#ef4444" },
                                        { label: "Completed", val: appointments.filter(a => a.status?.toLowerCase() === "completed").length, color: "#2563eb" },
                                    ].map((s, i) => (
                                        <div key={i} className={classes.statusRow}>
                                            <div className={classes.statusLabel}>
                                                <span className={classes.statusDot} style={{ background: s.color }}></span>
                                                {s.label}
                                            </div>
                                            <div className={classes.statusValue}>
                                                <strong>{s.val}</strong>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Newappointment isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAppointmentCreated={fetchAppointments} />
        </div>
    );
}